import React, { useMemo } from 'react';
import { DatePickerV2 } from './DatePickerV2';
import { constraintAnd, constraintMax, constraintMin, constraintTrue } from './constraints';
import { addDays, addMilliseconds, startOfDay, subMilliseconds } from 'date-fns';
import { CalendarConstraint, CalendarInputTarget, CalendarItem } from './types';
import { DateUnitPickerProps } from '../types';

/**
 * DatePickerV2를 DateUnitPicker와 동일한 props로 호환시키기 위한 컴포넌트입니다.
 */
export const DateUnitPickerCompat: React.FC<DateUnitPickerProps> = ({
  width,
  size,
  error,
  className,
  showRemoveButton,
  zIndex,
  displayMode,
  numberOfMonths,
  unit = 'week',
  dates,
  minDate,
  maxDate,
  disabled: disabledProp,
  autoCloseable = true,
  onOpen,
  onClose,
  onChange,
  tolerateInvalidValues = true,
  // weekStartsOn = 1,
}) => {
  const constraint = useMemo((): CalendarConstraint => {
    return constraintAnd(
      minDate ? constraintMin(startOfDay(minDate)) : constraintTrue(),
      maxDate ? constraintMax(addDays(startOfDay(maxDate), 1)) : constraintTrue(),
    );
  }, [minDate, maxDate]);

  const value = useMemo(() => {
    if (dates?.from && dates?.to) {
      return [{ id: '', from: dates.from, to: addMilliseconds(dates.to, 1) }];
    }
    return [];
  }, [dates?.from, dates?.to]);
  const defaultCursorItem = useMemo<Partial<CalendarItem> | null>(() => {
    if (dates?.from && !dates?.to) {
      return { from: dates.from };
    }
    if (!dates?.from && dates?.to) {
      return { to: addMilliseconds(dates.to, 1) };
    }
    return null;
  }, [dates?.from, dates?.to]);

  const disabled = useMemo(() => {
    if (Array.isArray(disabledProp)) {
      return disabledProp[0] && disabledProp[1];
    }
    return Boolean(disabledProp);
  }, [disabledProp]);
  const target = useMemo((): CalendarInputTarget | undefined => {
    if (!Array.isArray(disabledProp)) {
      return 'all';
    }
    if (disabledProp[0] && !disabledProp[1]) {
      return 'to';
    }
    if (!disabledProp[0] && disabledProp[1]) {
      return 'from';
    }
    return 'all';
  }, [disabledProp]);

  return (
    <DatePickerV2
      width={width}
      size={size}
      error={error}
      className={className}
      showRemoveButton={showRemoveButton}
      zIndex={zIndex}
      displayMode={displayMode}
      numberOfMonths={numberOfMonths}
      unit={unit}
      value={value}
      defaultCursorItem={defaultCursorItem}
      constraint={constraint}
      disabled={disabled}
      target={target}
      showTodayButton={unit === 'day'}
      showControls={!autoCloseable}
      showRange
      format='yyyy.MM.dd'
      onOpen={onOpen}
      onClose={onClose}
      onChange={(items) => {
        onChange?.(
          target === 'to' ? dates?.from : items[0]?.from,
          target === 'from' ? dates?.to : items[0]?.to ? subMilliseconds(items[0].to, 1) : undefined,
        );
      }}
      tolerateInvalidValues={tolerateInvalidValues}
    />
  );
};

export const DateUnitPicker = DateUnitPickerCompat;
